import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { fetchWithAuth } from '../../utils/api';
import { UserRole } from '../../types/auth';
import { getRoleDisplayName } from '../../utils/roleUtils';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { API_BASE_URL } from '../../config/constants';

// インターフェース定義
interface Employee {
  id: number;
  name: string;
}

interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  employeeId?: number | null;
  employee?: Employee | null;
}

const UserManagement: React.FC = () => {
  // 状態管理
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'viewer' as UserRole,
    password: '',
    employeeId: '',
  });

  // データ取得関数
  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/employees`);
      
      if (!response.ok) {
        throw new Error('従業員情報の取得に失敗しました');
      }
      
      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      console.error('従業員データの読み込み中にエラーが発生しました:', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/users`);
      
      if (!response.ok) {
        throw new Error('ユーザー情報の取得に失敗しました');
      }
      
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('ユーザーデータの読み込み中にエラーが発生しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 初期データ読み込み
  useEffect(() => {
    fetchUsers();
    fetchEmployees();
  }, []);

  // イベントハンドラ
  const handleOpenDialog = (user: User | null = null) => {
    setFormError(null);
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        password: '',
        employeeId: user.employeeId ? user.employeeId.toString() : '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        role: 'viewer',
        password: '',
        employeeId: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };
  
  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (formError) setFormError(null);
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // ユーザー保存処理
  const handleSubmit = async () => {
    try {
      // バリデーション
      if (!formData.name.trim()) {
        setFormError('名前を入力してください');
        return;
      }
      
      if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
        setFormError('有効なメールアドレスを入力してください');
        return;
      }
      
      if (!editingUser && !formData.password) {
        setFormError('パスワードを入力してください');
        return;
      }
      
      // APIリクエスト準備
      const url = editingUser 
        ? `${API_BASE_URL}/users/${editingUser.id}` 
        : `${API_BASE_URL}/users`;
      const method = editingUser ? 'PATCH' : 'POST';
      
      // ペイロード作成
      const payload: Record<string, any> = {
        email: formData.email,
        name: formData.name,
        role: formData.role,
      };
      
      // 従業員IDの処理
      payload.employeeId = formData.employeeId ? parseInt(formData.employeeId, 10) : null;
      
      // パスワード処理
      if (!editingUser || formData.password) {
        payload.password = formData.password;
      }
      
      // APIリクエスト送信
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      // レスポンス処理
      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 409) {
          setFormError('このメールアドレスは既に使用されています');
          return;
        }
        
        throw new Error(errorData.message || 'ユーザーの保存に失敗しました');
      }
      
      // 成功時の処理
      fetchUsers();
      handleCloseDialog();
    } catch (err) {
      console.error('ユーザー保存エラー:', err);
      setFormError('ユーザーの保存中にエラーが発生しました');
    }
  };

  // ユーザー削除処理
  const handleDelete = async (userId: number) => {
    if (!window.confirm('このユーザーを削除してもよろしいですか？')) {
      return;
    }
    
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/users/${userId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('ユーザーの削除に失敗しました');
      }
      
      fetchUsers();
    } catch (err) {
      console.error('ユーザー削除エラー:', err);
      alert('ユーザーの削除中にエラーが発生しました');
    }
  };

  // 表示用ラベル
  const roleLabels = {
    admin: '管理者',
    employee: '従業員',
    guest: 'ゲスト'
  };

  // UI レンダリング
  return (
    <Box>
      {/* ヘッダー部分 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">ユーザー管理</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          新規ユーザー
        </Button>
      </Box>
      
      {/* ローディングとエラー表示 */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        // ユーザー一覧テーブル
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>名前</TableCell>
                <TableCell>メールアドレス</TableCell>
                <TableCell>権限</TableCell>
                <TableCell>紐付け従業員</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getRoleDisplayName(user.role)}</TableCell>
                  <TableCell>
                    {user.employee ? user.employee.name : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpenDialog(user)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(user.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ユーザー追加/編集ダイアログ */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'ユーザーの編集' : '新規ユーザーの追加'}
        </DialogTitle>
        <DialogContent>
          {/* エラーメッセージ */}
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          
          {/* 基本情報フォーム */}
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="名前"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleTextFieldChange}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            name="email"
            label="メールアドレス"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleTextFieldChange}
            sx={{ mb: 2 }}
            required
          />
          
          {/* 権限選択 */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>権限</InputLabel>
            <Select
              name="role"
              value={formData.role}
              label="権限"
              onChange={handleSelectChange}
            >
              <MenuItem value="admin">管理者</MenuItem>
              <MenuItem value="editor">編集者</MenuItem>
              <MenuItem value="viewer">閲覧者</MenuItem>
              <MenuItem value="schedule_checker">空き確認</MenuItem>
            </Select>
          </FormControl>
          
          {/* 従業員選択 */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>紐付け従業員</InputLabel>
            <Select
              name="employeeId"
              value={formData.employeeId}
              label="紐付け従業員"
              onChange={handleSelectChange}
            >
              <MenuItem value="">紐付けなし</MenuItem>
              {loadingEmployees ? (
                <MenuItem disabled>読み込み中...</MenuItem>
              ) : (
                employees.map(employee => (
                  <MenuItem key={employee.id} value={employee.id.toString()}>
                    {employee.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          
          {/* パスワード入力 */}
          <TextField
            margin="dense"
            name="password"
            label={editingUser ? "新しいパスワード（変更する場合のみ）" : "パスワード"}
            type="password"
            fullWidth
            variant="outlined"
            value={formData.password}
            onChange={handleTextFieldChange}
            required={!editingUser}
          />
        </DialogContent>
        
        {/* アクションボタン */}
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">保存</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;